import { FETCH_REQUIRED_DATA } from "@app/bootup";
import { selectNav, setCollapsed } from "@app/nav";
import {
  hasOrganization,
  selectHasLabsFlagDetection,
  selectHasManyOrgs,
  selectOrganizationSelected,
} from "@app/organizations";
import { useDispatch, useSelector } from "@app/react";
import {
  APP_SERVICE_PATH,
  activityUrl,
  appsUrl,
  databaseUrl,
  endpointsUrl,
  environmentsUrl,
  homeUrl,
  impersonateUrl,
  labsUrl,
  llmGatewayUrl,
  logoutUrl,
  orgPickerUrl,
  searchUrl,
  servicesUrl,
  settingsTokenUrl,
  settingsUrl,
  ssoDirectUrl,
  stacksUrl,
  supportUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { SYSTEM_STATUS_ID } from "@app/system-status";
import { useSecurityControlsNav, useSsoRequiredBlock } from "@app/ui/hooks";
import { selectCanImpersonate, selectCurrentUser } from "@app/users";
import cn from "classnames";
import type { JSX } from "react";
import { useState } from "react";
import {
  Button,
  DialogTrigger,
  Popover as RAPopover,
} from "react-aria-components";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { AptibleLogo, AptibleLogoOnly } from "./aptible-logo";
import { Badge, type BadgeProps } from "./badge";
import {
  IconActivity,
  IconAlertCircle,
  IconAlertDot,
  IconBeaker,
  IconBox,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCylinder,
  IconDocs,
  IconEndpoint,
  IconGlobe,
  IconHamburger,
  IconHelp,
  IconHome,
  IconKey,
  IconLayers,
  IconLightbulb,
  IconLogout,
  IconSearch,
  IconService,
  IconSettings,
  IconShield,
  IconSubtleProvider,
  IconSwitchHorizontal,
  IconTerminal,
  IconWorkplace,
} from "./icons";
import { LinkNav, LinkNavDisabled } from "./link";

interface Props {
  collapsedOverride?: boolean;
  disableToggle?: boolean;
  onToggleCollapsed?: () => void;
}

interface NavItem {
  name: string;
  to: string;
  icon: JSX.Element;
  badge?: Omit<BadgeProps, "as">;
  disabled?: boolean;
  disabledTooltip?: string;
  isActive?: boolean;
}

interface SidebarAlert {
  key: string;
  label: string;
  message: string;
  action?: { label: string; to: string; external?: boolean };
}

const navRowBase = "block py-2 rounded-md hover:no-underline";
const navRowInactive = "hover:bg-black-50 hover:text-black text-black-500";

export const ApplicationSidebar = ({
  collapsedOverride,
  disableToggle = false,
  onToggleCollapsed,
}: Props) => {
  const dispatch = useDispatch();
  const { collapsed } = useSelector(selectNav);
  const effectiveCollapsed = collapsedOverride ?? collapsed;
  const systemStatus = useSelector((s) =>
    schema.cache.selectById(s, { id: SYSTEM_STATUS_ID }),
  );
  const hasSystemStatus =
    systemStatus?.description && systemStatus?.indicator !== "none";
  const bootupDone = useSelector(
    (s) => schema.loaders.selectById(s, { id: FETCH_REQUIRED_DATA }).isSuccess,
  );
  const navigate = useNavigate();
  const {
    canAccess: canSeeSecurityDashboard,
    destination: securityControlsDestination,
  } = useSecurityControlsNav();
  const org = useSelector(selectOrganizationSelected);
  const user = useSelector(selectCurrentUser);
  const canImpersonate = useSelector(selectCanImpersonate);
  const hasManyOrgs = useSelector(selectHasManyOrgs);
  const canSeeLabs = useSelector(selectHasLabsFlagDetection);
  const { blocked: ssoRequired } = useSsoRequiredBlock();

  const isServiceDetail =
    useMatch({ path: APP_SERVICE_PATH, end: false }) !== null;

  const alerts: SidebarAlert[] = [];
  if (bootupDone && !hasOrganization(org)) {
    alerts.push({
      key: "no-org",
      label: "No Organization selected",
      message:
        "You are not currently viewing an organization. Select one to continue.",
      action: { label: "Select an Organization", to: orgPickerUrl() },
    });
  } else if (bootupDone && ssoRequired) {
    alerts.push({
      key: "sso-required",
      label: "SSO Login Required",
      message: `Organization ${org.name} requires SSO. Sign in with SSO to continue accessing this organization.`,
      action: {
        label: "Login with SSO",
        to: ssoDirectUrl(org.id),
        external: true,
      },
    });
  }
  if (hasSystemStatus && systemStatus) {
    alerts.push({
      key: "system-status",
      label: "Platform Status",
      message: systemStatus.description,
    });
  }

  const topItems: NavItem[] = [
    { name: "Home", to: homeUrl(), icon: <IconHome /> },
    {
      name: "Activity",
      to: activityUrl(),
      icon: <IconActivity />,
    },
  ];

  const platformItems: NavItem[] = [
    { name: "Stacks", to: stacksUrl(), icon: <IconLayers /> },
    {
      name: "Environments",
      to: environmentsUrl(),
      icon: <IconGlobe />,
    },
    {
      name: "Apps",
      to: appsUrl(),
      icon: <IconBox />,
      isActive: isServiceDetail ? false : undefined,
    },
    {
      name: "Services",
      to: servicesUrl(),
      icon: <IconService />,
      isActive: isServiceDetail ? true : undefined,
    },
    {
      name: "Databases",
      to: databaseUrl(),
      icon: <IconCylinder />,
    },
    {
      name: "Endpoints",
      to: endpointsUrl(),
      icon: <IconEndpoint />,
    },
    {
      name: "Security & Compliance",
      to: securityControlsDestination,
      icon: <IconShield />,
      disabled: !canSeeSecurityDashboard,
      disabledTooltip: "Requires Full Visibility on any Environment",
    },
  ];

  const managedAiItems: NavItem[] = [
    {
      name: "LLM Gateway",
      to: llmGatewayUrl(),
      icon: <IconKey />,
      badge: { variant: "beta" as const, feature: "llm-keys" },
    },
  ];

  const renderItem = (item: NavItem) => (
    <div key={item.name} className="relative">
      {item.disabled ? (
        <LinkNavDisabled
          icon={item.icon}
          name={item.name}
          hideName={effectiveCollapsed}
          tooltipText={item.disabledTooltip ?? ""}
        />
      ) : (
        <LinkNav
          icon={item.icon}
          name={item.name}
          to={item.to}
          hideName={effectiveCollapsed}
          isActive={item.isActive}
        />
      )}
      {item.badge && !effectiveCollapsed ? (
        <Badge
          {...item.badge}
          className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
        />
      ) : null}
    </div>
  );

  const renderSectionHeader = (
    label: string,
    isCollapsed: boolean,
    onToggle: () => void,
  ) => (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-2 pt-4 pb-1 text-sm font-semibold text-black-300 uppercase tracking-wide hover:text-black cursor-pointer"
      aria-expanded={!isCollapsed}
    >
      <span>{label}</span>
      {isCollapsed ? (
        <IconChevronDown variant="sm" color="#888C90" />
      ) : (
        <IconChevronUp variant="sm" color="#888C90" />
      )}
    </button>
  );

  const bottomFlyoutItemBase =
    "flex items-center w-full px-3 py-2 gap-2 text-black hover:bg-black-50 hover:text-black rounded-md cursor-pointer text-left outline-none";

  const orgName = org.name ? org.name.slice(0, 20) : "";
  const userName = user.name ? user.name.slice(0, 20) : "";

  const [collapsedSections, setCollapsedSections] = useState(new Set<string>());
  const toggleSection = (name: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);

  return (
    <IconSubtleProvider value={true}>
      <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
        <div className="flex-1 min-h-0 flex flex-col pt-5 pb-4 overflow-y-auto">
          {disableToggle ? null : (
            <button
              aria-label={`${effectiveCollapsed ? "Expand" : "Collapse"} Menu`}
              type="button"
              className={`${
                effectiveCollapsed ? "mt-0 ml-4 my-4" : "absolute top-4 right-4"
              } hover:cursor-pointer`}
              onClick={() => {
                if (onToggleCollapsed) {
                  onToggleCollapsed();
                  return;
                }

                dispatch(setCollapsed({ collapsed: !collapsed }));
              }}
            >
              <IconHamburger color="#888C90" />
            </button>
          )}

          <div className="flex items-center flex-shrink-0 pl-4">
            <Link to={homeUrl()}>
              {effectiveCollapsed ? <AptibleLogoOnly /> : <AptibleLogo />}
            </Link>
          </div>

          {effectiveCollapsed ? null : (
            <div className="mt-4 px-3">
              <div
                className="border border-gray-200 bg-white rounded-lg px-2 py-1 flex items-center justify-between h-[36px] cursor-pointer hover:bg-black-50"
                onClick={() => navigate(searchUrl())}
                onKeyUp={() => navigate(searchUrl())}
              >
                <div className="flex items-center">
                  <IconSearch color="#888C90" variant="sm" />{" "}
                  <div className="ml-2 text-black-300">Search...</div>
                </div>
              </div>
            </div>
          )}

          <div
            className={`mt-2 flex-1 ${effectiveCollapsed ? "pl-2 pr-0" : "px-2"}`}
          >
            <nav className="bg-white">
              {topItems.map(renderItem)}

              {effectiveCollapsed ? (
                <>
                  {platformItems.map(renderItem)}
                  {managedAiItems.map(renderItem)}
                </>
              ) : (
                <>
                  {renderSectionHeader(
                    "Platform",
                    collapsedSections.has("platform"),
                    () => toggleSection("platform"),
                  )}
                  {collapsedSections.has("platform")
                    ? null
                    : platformItems.map(renderItem)}

                  {renderSectionHeader(
                    "Managed AI",
                    collapsedSections.has("managed-ai"),
                    () => toggleSection("managed-ai"),
                  )}
                  {collapsedSections.has("managed-ai")
                    ? null
                    : managedAiItems.map(renderItem)}
                </>
              )}
            </nav>
          </div>
        </div>

        <div className="flex-shrink-0 px-2 w-full pb-3">
          {!effectiveCollapsed && alerts.length > 0 ? (
            <div className="mb-2 flex flex-col gap-1">
              {alerts.map((alert) => (
                <DialogTrigger key={alert.key}>
                  <Button
                    className="flex items-center gap-2 h-[30px] w-full pl-3 pr-2 py-2 rounded border border-orange-300 bg-orange-100 shadow-[0px_1px_2px_rgba(0,0,0,0.06)] font-mono font-bold text-[10px] leading-[14px] uppercase text-black outline-none cursor-pointer text-left"
                    aria-label={alert.label}
                  >
                    <IconAlertDot variant="sm" className="shrink-0" />
                    <span className="truncate">{alert.label}</span>
                  </Button>
                  <RAPopover placement="top" offset={8}>
                    <div className="bg-orange-100 border border-orange-300 rounded shadow-[2px_2px_5px_rgba(0,0,0,0.05)] p-3 w-[280px] outline-none flex flex-col gap-2">
                      <div className="flex items-start gap-2">
                        <IconAlertDot
                          variant="sm"
                          className="shrink-0 mt-0.5"
                        />
                        <div className="flex-1 text-[12px] leading-[16px] text-black">
                          {alert.message}
                          {alert.key === "system-status" ? (
                            <>
                              {" "}
                              <a
                                href="https://status.aptible.com"
                                target="_blank"
                                rel="noreferrer"
                                className="text-indigo underline"
                              >
                                See Status Page for more details
                              </a>
                              .
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </RAPopover>
                </DialogTrigger>
              ))}
            </div>
          ) : null}

          {canImpersonate ? (
            <LinkNav
              to={impersonateUrl()}
              icon={<IconAlertCircle />}
              name="Impersonate"
              hideName={effectiveCollapsed}
            />
          ) : null}

          <LinkNav
            to={supportUrl()}
            icon={<IconHelp />}
            name="Contact Support"
            hideName={effectiveCollapsed}
          />

          <DialogTrigger isOpen={resourcesOpen} onOpenChange={setResourcesOpen}>
            <Button
              className={cn(
                "flex items-center w-full py-2 rounded-md outline-none cursor-pointer hover:no-underline",
                effectiveCollapsed ? "pl-2 pr-0" : "px-2",
                resourcesOpen ? "bg-black-50 text-black" : navRowInactive,
              )}
              aria-label="Resources"
            >
              <div className="flex items-center flex-1 min-w-0">
                <div className="mr-2" aria-hidden="true">
                  <IconLightbulb />
                </div>
                {effectiveCollapsed ? null : <div>Resources</div>}
              </div>
              {effectiveCollapsed ? null : (
                <IconChevronRight variant="sm" color="#888C90" />
              )}
            </Button>
            <RAPopover placement="right" offset={8}>
              <div className="bg-white border border-black-100 rounded-md shadow-[2px_2px_5px_rgba(0,0,0,0.05)] p-2 min-w-[205px] outline-none flex flex-col gap-1">
                {[
                  {
                    key: "access-tokens",
                    label: "Access Tokens",
                    icon: <IconKey />,
                    to: settingsTokenUrl(),
                    external: false,
                  },
                  {
                    key: "install-cli",
                    label: "Install CLI",
                    icon: <IconTerminal />,
                    to: "https://www.aptible.com/docs/cli",
                    external: true,
                  },
                  {
                    key: "view-docs",
                    label: "View Docs",
                    icon: <IconDocs />,
                    to: "https://aptible.com/docs",
                    external: true,
                  },
                ].map((item) =>
                  item.external ? (
                    <a
                      key={item.key}
                      href={item.to}
                      target="_blank"
                      rel="noreferrer"
                      className={bottomFlyoutItemBase}
                    >
                      <span className="shrink-0" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </a>
                  ) : (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={bottomFlyoutItemBase}
                    >
                      <span className="shrink-0" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </Link>
                  ),
                )}
              </div>
            </RAPopover>
          </DialogTrigger>

          {effectiveCollapsed ? null : (
            <div className="my-2 border-t border-black-100" />
          )}

          <DialogTrigger isOpen={orgMenuOpen} onOpenChange={setOrgMenuOpen}>
            <Button
              className={cn(
                navRowBase,
                effectiveCollapsed ? "pl-2 pr-0" : "px-2",
                "w-full flex items-center justify-between outline-none cursor-pointer",
                orgMenuOpen ? "bg-black-50 text-black" : navRowInactive,
              )}
              aria-label="Organization menu"
            >
              <div className="flex items-center min-w-0">
                <div
                  className={cn(
                    "flex items-center justify-center rounded-md bg-indigo-100 p-1.5",
                    effectiveCollapsed ? undefined : "mr-3",
                  )}
                  aria-hidden="true"
                >
                  <IconWorkplace />
                </div>
                {effectiveCollapsed ? null : (
                  <div className="min-w-0 text-left leading-tight">
                    <div className="text-black-500 font-medium truncate">
                      {orgName}
                    </div>
                    <div className="text-gray-400 text-sm truncate">
                      {userName}
                    </div>
                  </div>
                )}
              </div>
              {effectiveCollapsed ? null : (
                <IconChevronRight variant="sm" color="#888C90" />
              )}
            </Button>
            <RAPopover placement="right" offset={8}>
              <div className="bg-white border border-black-100 rounded-md shadow-[2px_2px_5px_rgba(0,0,0,0.05)] p-2 min-w-[205px] outline-none flex flex-col gap-1">
                {hasManyOrgs ? (
                  <Link to={orgPickerUrl()} className={bottomFlyoutItemBase}>
                    <span className="shrink-0" aria-hidden="true">
                      <IconSwitchHorizontal />
                    </span>
                    <span>Switch Organization</span>
                  </Link>
                ) : null}
                {canSeeLabs ? (
                  <Link to={labsUrl()} className={bottomFlyoutItemBase}>
                    <span className="shrink-0" aria-hidden="true">
                      <IconBeaker />
                    </span>
                    <span>Labs</span>
                  </Link>
                ) : null}
                <Link to={settingsUrl()} className={bottomFlyoutItemBase}>
                  <span className="shrink-0" aria-hidden="true">
                    <IconSettings />
                  </span>
                  <span>Settings</span>
                </Link>
                <Link to={logoutUrl()} className={bottomFlyoutItemBase}>
                  <span className="shrink-0" aria-hidden="true">
                    <IconLogout />
                  </span>
                  <span>Log Out</span>
                </Link>
              </div>
            </RAPopover>
          </DialogTrigger>
        </div>
      </div>
    </IconSubtleProvider>
  );
};
